import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { Download, Edit2, Trash, Upload } from 'react-feather';
import { usePrefixTranslation } from '../../i18n';
import { theme } from '../../theme/theme';
import { downloadFileDialog, fileFilter, fileToDataUrl, openFileDialog } from '../../util/file';
import { Button, IconButton } from './';
import picturePlaceholder from './picturePlaceholder.svg';

const useComponentClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: 5,
        borderRadius: 5,
        background: theme.colors.neutral[6],
        divShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
    },
    pictureSection: {
        display: 'flex',
        padding: 5,
        '&.editable': {
            flexDirection: 'column',
        },
        '&.readonly': {
            flexDirection: 'row',
            flex: 1,
        },
    },
    picture: {
        borderRadius: 5,
        '&.editable': {
            width: '100%',
            maxHeight: 450,
            marginBottom: 10,
            objectFit: 'cover',
        },
        '&.readonly': {
            maxWidth: '33%',
            marginRight: 10,
            objectFit: 'contain',
        },
    },
    pictureMenu: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        wordBreak: 'break-all',
    },
    name: {
        flex: 1,
        '&.editable': {
            color: theme.colors.neutral[2],
            ...theme.typography.content.label.default,
        },
        '&.readonly': {
            color: theme.colors.darkBlue[5],
            ...theme.typography.headings.h4,
            marginBottom: 0,
        },
    },
    icon: {
        color: theme.colors.neutral[2],
    },
});

export interface DisplayPicture {
    title: string;
    url: string;
}
/**
 * Filenames are really complicated to handle in some situations, due to scarce information.
 * This property saves filenames among re-renders so they can be reused if provided in previous uses of this component.
 */
const filenames: { [url: string]: string } = {};

/**
 * Widget for displaying and uploading pictures.
 *
 * @param props.pictures list of current pictures
 * @param props.limit maximum allowed number of pictures
 * @param props.editable set the component to the editable mode, allowing uploads, edits and discards
 * @param props.onChange callback called when the list of pictures change
 * @param props.uploadFunction react ref to be populated with the internal image uploader upload function
 */
export const PictureUploader = (props: {
    pictures: DisplayPicture[];
    limit: number;
    editable: boolean;
    onChange?: (pictures: string[]) => void;
    uploadRef?: React.MutableRefObject<(() => Promise<void>) | undefined>;
}) => {
    const pictureUrls = props.pictures.map(pic => pic.url);
    const pictureTitles = props.pictures.map(pic => pic.title);

    const classes = useComponentClasses();
    const { t } = usePrefixTranslation('global', { global: 'PictureUploader' });
    const pictures = pictureUrls.filter(p => p != undefined);
    pictures.forEach(p => {
        if (p.startsWith('data:') && filenames[p.slice(0, 100)] == undefined) filenames[p.slice(0, 100)] = '';
        else if (!p.startsWith('data:')) filenames[p] = p.split('/').pop() ?? 'image';
    });
    const status = {
        editable: props.editable,
        readonly: !props.editable,
    };

    const addPictures = async (files: File[]) => {
        const pictureFiles = fileFilter(files, /image.*/);
        const availableSpaces = props.limit == undefined ? Infinity : props.limit - pictures.length;
        const newPictures = await Promise.all(pictureFiles.slice(0, availableSpaces).map(f => fileToDataUrl(f)));
        newPictures.forEach((p, i) => (filenames[p.slice(0, 100)] = pictureFiles[i].name));
        props.onChange?.([...pictureTitles, ...newPictures]);
    };

    const editPicture = async (index: number, files: File[]) => {
        const pictureFile = fileFilter(files, /image.*/)[0];
        if (!pictureFile) return;
        const picture = await fileToDataUrl(pictureFile);
        filenames[picture.slice(0, 100)] = pictureFile.name;
        const nextPictures = [...pictureTitles];
        nextPictures.splice(index, 1, picture);
        props.onChange?.(nextPictures);
    };

    const deletePicture = (index: number) => {
        const nextPictures = [...pictureTitles];
        nextPictures.splice(index, 1);
        props.onChange?.(nextPictures);
    };

    const pictureName = (picture: string) => picture.slice(0, 100);

    if (props.uploadRef) props.uploadRef.current = async () => addPictures(await openFileDialog('image/*'));

    return (
        <div
            className={classes.root}
            onDragOver={event => event.preventDefault()}
            onDrop={event => (event.preventDefault(), addPictures([...event.dataTransfer.files]))}
        >
            {pictures.map((picture, i) => (
                <div key={i} className={clsx(classes.pictureSection, status)}>
                    <img className={clsx(classes.picture, status)} src={picture} alt={t('picture-alt', { index: i })} />
                    <div className={classes.pictureMenu}>
                        <span className={clsx(classes.name, status)}>{pictureName(picture)}</span>
                        <IconButton
                            className={classes.icon}
                            size='small'
                            title={t('icon-download')}
                            onClick={() => downloadFileDialog(picture)}
                        >
                            <Download />
                        </IconButton>
                        {props.editable && (
                            <IconButton
                                className={classes.icon}
                                size='small'
                                title={t('icon-edit')}
                                onClick={async () => editPicture(i, await openFileDialog('image/*'))}
                            >
                                <Edit2 />
                            </IconButton>
                        )}
                        {props.editable && (
                            <IconButton
                                className={classes.icon}
                                size='small'
                                title={t('icon-delete')}
                                onClick={() => deletePicture(i)}
                            >
                                <Trash />
                            </IconButton>
                        )}
                    </div>
                </div>
            ))}
            <div className={clsx(classes.pictureSection, status)}>
                {pictures.length === 0 && (
                    <img
                        className={clsx(classes.picture, status)}
                        src={picturePlaceholder}
                        alt={t('picture-alt', { index: -1 })}
                    />
                )}
                <div className={classes.pictureMenu}>
                    {pictures.length === 0 && <span className={clsx(classes.name, status)}>{t('picture-absent')}</span>}
                    {props.editable && (props.limit == undefined || pictures.length < props.limit) && (
                        <Button
                            size='medium'
                            endIcon={<Upload />}
                            onClick={async () => addPictures(await openFileDialog('image/*'))}
                        >
                            {t('button-upload')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
